// src/Pages/Creator/PayoutsInvoicing/index.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tabs,
  Tab,
  Snackbar,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  useMediaQuery,
  styled,
  Pagination, // Material-UI Pagination for page control
} from '@mui/material';
import { useQuery } from 'react-query';
import { useCreatorAuth } from '../../../Hooks/creator-use-auth';
import API from '../../../API';
import { Link } from 'react-router-dom';
import { saveAs } from 'file-saver';
import DownloadIcon from '@mui/icons-material/Download';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';

// ---------- Styled helpers ----------
const StatusChip = styled('span')(({ theme, status }) => {
  const map = {
    pending: theme.palette.warning.main,
    approved: theme.palette.success.main,
    completed: theme.palette.success.dark,
    cancelled: theme.palette.error.main,
    paid: theme.palette.info.main,
    scheduled: theme.palette.info.light,
  };
  return {
    display: 'inline-block',
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.getContrastText(map[status] || theme.palette.grey[400]),
    backgroundColor: map[status] || theme.palette.grey[400],
    padding: '2px 8px',
    borderRadius: 8,
    textTransform: 'capitalize',
  };
});

const PayoutsInvoicing = () => {
  const { creatorToken } = useCreatorAuth();
  const username = creatorToken?.creator_user?.username;
  const isMobile = useMediaQuery('(max-width:600px)');

  // ------------------- State -------------------
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState({ payoutId: null, method: '' });
  const [activeTab, setActiveTab] = useState('all'); // "all" | "payouts" | "invoices"
  const [statusFilter, setStatusFilter] = useState('all'); // default filter is "all"
  const [page, setPage] = useState(1); // pagination state, 6 items per page
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [stripeAccounts, setStripeAccounts] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // "grid" | "list"

  // ------------------- Queries -------------------
  const {
    isLoading,
    error,
    data: financials,
  } = useQuery(['creator', 'financials'], () => API.creatorConnect.getFinancials(username), {
    keepPreviousData: true,
  });

  // Fetch Stripe accounts once
  useEffect(() => {
    const fetchStripeBankAccounts = async () => {
      try {
        const data = await API.bank.getStripeBankAccounts({
          entity_type: 'creator',
          entity_id: username,
        });
        setStripeAccounts(data);
      } catch (err) {
        console.error('Error fetching Stripe bank accounts:', err);
      }
    };
    if (username) fetchStripeBankAccounts();
  }, [username]);

  // ------------------- Helpers -------------------
  const handleOpenPaymentDialog = (payoutId) => {
    setPaymentDialogOpen(true);
    setPaymentMethod({ payoutId, method: '' });
  };
  const handleClosePaymentDialog = () => setPaymentDialogOpen(false);

  const handleConfirmPayment = async () => {
    try {
      await API.creatorConnect.confirmPayment({
        username,
        payoutId: paymentMethod.payoutId,
        paymentMethod: paymentMethod.method,
      });
      setSnackbarMessage('Payment confirmed – payout approved!');
      setSnackbarOpen(true);
      handleClosePaymentDialog();
    } catch (err) {
      alert(err);
    }
  };

  // When switching tabs or updating the status filter, reset page to 1
  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    setPage(1);
  };
  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };
  const handleCloseSnackbar = (_, r) => r !== 'clickaway' && setSnackbarOpen(false);

  // CSV export
  const handleDownloadCSV = () => {
    const csvData = [
      ['ID', 'Type', 'Amount', 'Status', 'Payout Date', 'Blitzpay', 'Fee', 'Code', 'Notes'],
      ...(financials?.invoices || []).map((i) => [
        i.id,
        'Invoice',
        i.amount?.toFixed(2),
        i.status,
        i.payout_date,
        i.blitzpay ? 'Yes' : 'No',
        ((i.interest_rate / 100) * i.amount).toFixed(2),
        i.unique_code,
        i.notes,
      ]),
      ...(financials?.payouts || []).map((p) => [
        p.id,
        'Payout',
        p.amount?.toFixed(2),
        p.status,
        p.payout_date,
        p.blitzpay ? 'Yes' : 'No',
        ((p.interest_rate / 100) * p.amount).toFixed(2),
        p.unique_code,
        p.notes,
      ]),
    ];
    const csv = csvData.map((row) => row.join(',')).join('\n');
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), `Financial_${Date.now()}.csv`);
  };

  // ------------------- Derived data -------------------
  if (isLoading)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  if (error)
    return (
      <Typography sx={{ color: 'error.main' }}>
        Error fetching financials: {error.message}
      </Typography>
    );

  // Sort and tag records
  const payouts = [...(financials?.payouts || [])].sort(
    (a, b) => new Date(b.payout_date) - new Date(a.payout_date),
  );
  const invoices = [...(financials?.invoices || [])].sort(
    (a, b) => new Date(b.payout_date) - new Date(a.payout_date),
  );
  const allData = [
    ...payouts.map((p) => ({ ...p, recordType: 'payout' })),
    ...invoices.map((i) => ({ ...i, recordType: 'invoice' })),
  ].sort((a, b) => new Date(b.payout_date) - new Date(a.payout_date));

  // This function filters items by a chosen status (if not "all").
  const applyStatusFilter = (arr) =>
    statusFilter === 'all'
      ? arr
      : arr.filter(
          (it) =>
            it.status &&
            it.status.trim().toLowerCase() === statusFilter // using trim() for safety
        );

  // Set displayed data based on activeTab
  const displayedData =
    activeTab === 'all'
      ? applyStatusFilter(allData)
      : activeTab === 'payouts'
      ? applyStatusFilter(payouts)
      : applyStatusFilter(invoices);

  // --- Filtering Logic for "all" tab default behavior ---
  // When on the "All Activity" tab and the dropdown is "all", we want to hide cancelled items.
  // Otherwise (e.g. in the payouts/invoices tabs or when a specific status is chosen) we show the items as returned.
  const filteredData =
    activeTab === 'all' && statusFilter === 'all'
      ? displayedData.filter(
          (item) => item.status && item.status.trim().toLowerCase() !== 'cancelled'
        )
      : displayedData;

  // Pagination: 6 items per page
  const itemsPerPage = 6;
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const pagedData = filteredData.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  // Earnings calculations (using raw payouts/invoices arrays)
  const pendingEarnings = payouts
    .filter((p) => p.status && p.status.trim().toLowerCase() === 'pending')
    .reduce((s, p) => s + (p.amount || 0), 0);
  const approvedEarnings = payouts
    .filter((p) => p.status && p.status.trim().toLowerCase() === 'approved')
    .reduce((s, p) => s + (p.amount || 0), 0);
  const totalEarnings = pendingEarnings + approvedEarnings;
  const unverifiedFunds = invoices
    .filter((i) => i.status && i.status.trim().toLowerCase() === 'pending')
    .reduce((s, i) => s + (i.amount || 0), 0);

  // ------------------- Render -------------------
  return (
    <Box sx={{ p: 2 }}>
      {/* ---------- Header ---------- */}
      <Paper
        elevation={1}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Blitz Balance
          </Typography>
          <Typography variant="h6" color="text.secondary" mt={1}>
            Approved + Pending: ${totalEarnings.toFixed(2)}
          </Typography>
          <Typography variant="body2" mt={0.5}>
            Unverified (Pending Invoices): ${unverifiedFunds.toFixed(2)}
          </Typography>
        </Box>

        <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
          <Button variant="contained" component={Link} to="/creatorinvoicing" sx={{ mr: 2 }}>
            Invoice Someone
          </Button>
          <Typography variant="caption" display="block" mt={1}>
            Set up BlitzPay in settings for direct deposits.
          </Typography>
        </Box>
      </Paper>

      {/* ---------- Tabs + View toggle ---------- */}
      <Paper elevation={1} square sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ flexGrow: 1 }}
        >
          <Tab label="All Activity" value="all" />
          <Tab label="Payouts" value="payouts" />
          <Tab label="Invoices" value="invoices" />
        </Tabs>

        <IconButton onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}>
          {viewMode === 'grid' ? <ViewListIcon /> : <ViewModuleIcon />}
        </IconButton>
      </Paper>

      {/* ---------- Filters + CSV ---------- */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <FormControl size="small">
          <Select value={statusFilter} onChange={handleStatusChange}>
            {['all', 'pending', 'approved', 'completed', 'cancelled', 'paid', 'scheduled'].map((s) => (
              <MenuItem key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadCSV}>
          Download CSV
        </Button>
      </Box>

      {/* ---------- Data section ---------- */}
      {filteredData.length === 0 ? (
        <Typography>No records found.</Typography>
      ) : viewMode === 'list' ? (
        // ----------- LIST VIEW -----------
        <Paper elevation={0} sx={{ p: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Payout Date</TableCell>
                  <TableCell>Actions</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedData.map((item) => {
                  const isInvoice = item.recordType === 'invoice';
                  const statusKey = item.status ? item.status.trim().toLowerCase() : 'other';
                  return (
                    <TableRow key={item.id}>
                      <TableCell>
                        <StatusChip status={statusKey}>{item.status}</StatusChip>
                      </TableCell>
                      <TableCell>${(item.amount || 0).toFixed(2)}</TableCell>
                      <TableCell>{item.payout_date}</TableCell>
                      <TableCell>
                        {isInvoice ? (
                          <Button
                            size="small"
                            variant="contained"
                            component={Link}
                            to={`/creatorinvoice/${item.id}`}
                          >
                            View Invoice
                          </Button>
                        ) : item.status &&
                          item.status.trim().toLowerCase() === 'pending' ? (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleOpenPaymentDialog(item.id)}
                          >
                            Approve Payment
                          </Button>
                        ) : (
                          <Typography variant="body2">
                            {item.blitzpay ? 'BlitzPay' : 'Net30'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{item.notes}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        // ----------- GRID VIEW -----------
        <Grid container spacing={2}>
          {pagedData.map((item) => {
            const isInvoice = item.recordType === 'invoice';
            const statusKey = item.status ? item.status.trim().toLowerCase() : 'other';

            return (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 3,
                    transition: 'transform 0.25s, box-shadow 0.25s',
                    ':hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6,
                    },
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1,
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight="bold">
                        {isInvoice ? 'Invoice' : 'Payout'} #{item.id}
                      </Typography>
                      <StatusChip status={statusKey}>{item.status}</StatusChip>
                    </Box>

                    <Typography variant="h5" fontWeight="bold" mb={1}>
                      ${(item.amount || 0).toFixed(2)}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      Payout Date: {item.payout_date || '—'}
                    </Typography>

                    {item.notes && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1, wordBreak: 'break-word' }}
                      >
                        {item.notes}
                      </Typography>
                    )}
                  </CardContent>

                  <CardActions sx={{ px: 2, pb: 2 }}>
                    {isInvoice ? (
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        component={Link}
                        to={`/creatorinvoice/${item.id}`}
                      >
                        View Invoice
                      </Button>
                    ) : item.status &&
                      item.status.trim().toLowerCase() === 'pending' ? (
                      <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        onClick={() => handleOpenPaymentDialog(item.id)}
                      >
                        Approve Payment
                      </Button>
                    ) : (
                      <Button fullWidth variant="text" size="small" disabled>
                        {item.blitzpay ? 'BlitzPay' : 'Net30'}
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ---------- Pagination Controls ---------- */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* ---------- Payment Dialog ---------- */}
      <Dialog open={paymentDialogOpen} onClose={handleClosePaymentDialog}>
        <DialogTitle>Select Payment Method</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Choose your preferred payment method.
          </DialogContentText>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <Select
              value={paymentMethod.method}
              onChange={(e) =>
                setPaymentMethod((prev) => ({ ...prev, method: e.target.value }))
              }
            >
              <MenuItem value="BLITZPAY">BlitzPay (Instant)</MenuItem>
              <MenuItem value="NET30">Net 30</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaymentDialog}>Cancel</Button>
          <Button onClick={handleConfirmPayment}>Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* ---------- Snackbar ---------- */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        action={
          <Button color="inherit" size="small" onClick={handleCloseSnackbar}>
            Close
          </Button>
        }
      />
    </Box>
  );
};

export default PayoutsInvoicing;
